/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  ExportJob,
  GetAdminV1StoresStoreIdExports200,
  GetAdminV1StoresStoreIdExportsIdDownloadPathParameters,
  GetAdminV1StoresStoreIdExportsIdPathParameters,
  GetAdminV1StoresStoreIdExportsParams,
  GetAdminV1StoresStoreIdExportsPathParameters,
  GetAdminV1StoresStoreIdImports200,
  GetAdminV1StoresStoreIdImportsIdPathParameters,
  GetAdminV1StoresStoreIdImportsParams,
  GetAdminV1StoresStoreIdImportsPathParameters,
  GetAdminV1StoresStoreIdImportsSampleParams,
  GetAdminV1StoresStoreIdImportsSamplePathParameters,
  ImportJob,
  PostAdminV1StoresStoreIdExportsBody,
  PostAdminV1StoresStoreIdExportsPathParameters,
  PostAdminV1StoresStoreIdImportsParams,
  PostAdminV1StoresStoreIdImportsPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Export jobs of the store, newest first
 */
export const getAdminV1StoresStoreIdExports = (
    { storeId }: GetAdminV1StoresStoreIdExportsPathParameters,
    params?: GetAdminV1StoresStoreIdExportsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdExports200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdExports200>(
      {url: `/admin/v1/stores/${storeId}/exports`, method: 'GET',
        params
    },
      options);
    }
  /**
 * orders_csv takes an optional placed-date range (store days); products_csv exports the organization's catalog in the Shopify-compatible layout; customers_csv, inventory_csv and newsletter_csv (subscribers with status and consent) are per store; conversations_csv (needs conversations.read too) takes the same date range on when the conversation started. The worker writes the file; poll the job until `ready`, then download it. Files expire after 7 days
 * @summary Queue a CSV export
 */
export const postAdminV1StoresStoreIdExports = (
    { storeId }: PostAdminV1StoresStoreIdExportsPathParameters,
    postAdminV1StoresStoreIdExportsBody: BodyType<PostAdminV1StoresStoreIdExportsBody>,
 options?: SecondParameter<typeof customInstance<ExportJob>>,) => {
      return customInstance<ExportJob>(
      {url: `/admin/v1/stores/${storeId}/exports`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdExportsBody
    },
      options);
    }
  /**
 * @summary One export job
 */
export const getAdminV1StoresStoreIdExportsId = (
    { storeId, id }: GetAdminV1StoresStoreIdExportsIdPathParameters,
 options?: SecondParameter<typeof customInstance<ExportJob>>,) => {
      return customInstance<ExportJob>(
      {url: `/admin/v1/stores/${storeId}/exports/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * text/csv attachment. 409 while the job is queued or running, 404 once the file has expired
 * @summary Download the CSV of a completed export
 */
export const getAdminV1StoresStoreIdExportsIdDownload = (
    { storeId, id }: GetAdminV1StoresStoreIdExportsIdDownloadPathParameters,
 options?: SecondParameter<typeof customInstance<string>>,) => {
      return customInstance<string>(
      {url: `/admin/v1/stores/${storeId}/exports/${id}/download`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Import jobs of the store, newest first
 */
export const getAdminV1StoresStoreIdImports = (
    { storeId }: GetAdminV1StoresStoreIdImportsPathParameters,
    params?: GetAdminV1StoresStoreIdImportsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdImports200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdImports200>(
      {url: `/admin/v1/stores/${storeId}/imports`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Send the file as the request body with Content-Type text/csv. products_csv reads the Shopify product layout (Handle, Title, Body (HTML), Vendor, Type, Tags, Published, Option1 Name/Value…, Variant SKU (blank: the product type's SKU rule writes one), SKU Root (Nucleus: the style number; blank: the rule allocates one), Variant Weight with Variant Weight Unit (g, kg, dwt, ozt, oz or lb, kept as given) or else Variant Grams, Variant Price, Variant Compare At Price, Variant Inventory Tracker, Variant Inventory Qty, Variant Requires Shipping, Variant Taxable) plus Nucleus columns (Fulfillment Mode, Lead Time Min, Lead Time Max, Requires Design Approval, Track Inventory, Variant Cost, and for diamond settings Configurator Shapes, Configurator Stone Types, Configurator Carat Min, Configurator Carat Max); a row with Configurator Shapes makes the product a setting that takes a chosen diamond; options must name attributes and values of the dictionary; Image Src (with Image Position and Image Alt Text) and Variant Image are fetched from their URLs into the media library. An existing handle is reported as a row error, or with onExisting=update its product is updated: filled cells replace stored values, blank cells keep them, variants match by SKU (a retired SKU still finds the variant that carried it; resolvedByHistory counts those rows), new option values are added, images are replaced when the file carries any. customers_csv (Email, First Name, Last Name, Phone, Tags, Accepts Marketing, Notes) upserts by email. inventory_csv (SKU or Variant ID, Location, Quantity) sets counted stock. The header is checked at upload; row errors land on the job
 * @summary Upload a CSV and queue its import
 */
export const postAdminV1StoresStoreIdImports = (
    { storeId }: PostAdminV1StoresStoreIdImportsPathParameters,
    params: PostAdminV1StoresStoreIdImportsParams,
 options?: SecondParameter<typeof customInstance<ImportJob>>,) => {
      return customInstance<ImportJob>(
      {url: `/admin/v1/stores/${storeId}/imports`, method: 'POST',
        params
    },
      options);
    }
  /**
 * text/csv attachment with the accepted header and example rows built from the organization's own dictionary (product type, option attributes, SKUs, stock location), so the file imports unchanged
 * @summary Download a sample CSV for an import kind
 */
export const getAdminV1StoresStoreIdImportsSample = (
    { storeId }: GetAdminV1StoresStoreIdImportsSamplePathParameters,
    params: GetAdminV1StoresStoreIdImportsSampleParams,
 options?: SecondParameter<typeof customInstance<string>>,) => {
      return customInstance<string>(
      {url: `/admin/v1/stores/${storeId}/imports/sample`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary One import job with its progress and row errors
 */
export const getAdminV1StoresStoreIdImportsId = (
    { storeId, id }: GetAdminV1StoresStoreIdImportsIdPathParameters,
 options?: SecondParameter<typeof customInstance<ImportJob>>,) => {
      return customInstance<ImportJob>(
      {url: `/admin/v1/stores/${storeId}/imports/${id}`, method: 'GET'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdExportsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdExports>>>
export type PostAdminV1StoresStoreIdExportsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdExports>>>
export type GetAdminV1StoresStoreIdExportsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdExportsId>>>
export type GetAdminV1StoresStoreIdExportsIdDownloadResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdExportsIdDownload>>>
export type GetAdminV1StoresStoreIdImportsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdImports>>>
export type PostAdminV1StoresStoreIdImportsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdImports>>>
export type GetAdminV1StoresStoreIdImportsSampleResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdImportsSample>>>
export type GetAdminV1StoresStoreIdImportsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdImportsId>>>
